/**
 * Utility functions for formatting data in the ComparisonModals components
 */

/**
 * Format speed from kbps to a human-readable string (Mbps or Gbps)
 * @param {number} speedKbps - Speed in kilobits per second
 * @returns {string} - Formatted speed string
 */
export const formatSpeed = (speedKbps) => {
  if (!speedKbps && speedKbps !== 0) return '0 Mbps';
  const speedMbps = speedKbps / 1024;
  return speedMbps >= 1000
    ? `${(speedMbps / 1000).toFixed(1)} Gbps`
    : `${speedMbps} Mbps`;
};

/**
 * Format price with currency symbol
 * @param {number} price - Price value
 * @param {string} currency - Currency symbol (default: 'R')
 * @returns {string} - Formatted price string
 */
export const formatPrice = (price, currency = 'R') => {
  return `${currency}${price.toFixed(2)}`;
};

/**
 * Determine if a package is suitable for specific use cases
 * @param {Object} pkg - Package object
 * @returns {string|null} - Usage profile or null if none matches
 */
export const getUsageProfile = (pkg) => {
  if (!pkg || !pkg["Download speed (kbps)"] || !pkg["Upload speed (kbps)"]) return null;
  
  const downloadSpeed = pkg["Download speed (kbps)"] / 1024; // Convert to Mbps
  const uploadSpeed = pkg["Upload speed (kbps)"] / 1024; // Convert to Mbps
  
  const profiles = [];
  
  // Streaming profile
  if (downloadSpeed >= 100) {
    profiles.push('Great for Streaming');
  } else if (downloadSpeed >= 50) {
    profiles.push('Good for Streaming');
  }
  
  // Gaming profile
  if (downloadSpeed >= 50 && uploadSpeed >= 20) {
    profiles.push('Great for Gaming');
  }
  
  // Work from home profile
  if (uploadSpeed >= 20 && downloadSpeed >= 50) {
    profiles.push('Perfect for WFH');
  } else if (uploadSpeed >= 10 && downloadSpeed >= 25) {
    profiles.push('Good for WFH');
  }
  
  // If symmetrical connection
  if (downloadSpeed === uploadSpeed && downloadSpeed >= 100) {
    profiles.push('Symmetrical');
  }
  
  return profiles.length > 0 ? profiles[0] : null;
};

/**
 * Get CSS classes for a package card based on its type and profile
 * @param {Object} pkg - Package object
 * @returns {Object} - Object with CSS class strings
 */
export const getPackageCardClasses = (pkg) => {
  const isBusinessPackage = pkg.connectionType === 'FTTB';
  const usageProfile = getUsageProfile(pkg);
  
  return {
    card: `bg-gray-800 rounded-xl overflow-hidden border ${
      isBusinessPackage
        ? 'border-blue-500/30 shadow-lg shadow-blue-500/10' // Business packages
        : usageProfile
          ? 'border-purple-500/30 shadow-lg shadow-purple-500/10' // Home packages with usage profile
          : 'border-purple-500/20 border-opacity-50' // Home packages without usage profile
    }`,
    badge: `w-full py-1 text-center text-xs font-medium ${
      isBusinessPackage
        ? 'bg-blue-600/20 text-blue-400'
        : 'bg-purple-600/20 text-purple-400'
    }`,
    icon: `h-5 w-5 rounded-full mr-2 flex items-center justify-center ${
      isBusinessPackage
        ? 'bg-blue-600/20'
        : 'bg-purple-600/20'
    }`,
    profileBadge: `text-xs px-2 py-0.5 rounded-full flex items-center ${
      isBusinessPackage
        ? 'bg-blue-600/20 text-blue-400'
        : 'bg-purple-600/20 text-purple-400'
    }`,
    connectionType: `flex items-center justify-center p-2 rounded-lg mb-2 ${
      isBusinessPackage
        ? 'bg-blue-600/10 text-blue-400'
        : 'bg-purple-600/10 text-purple-400'
    }`,
    progressBar: `h-full ${
      isBusinessPackage
        ? 'bg-gradient-to-r from-blue-500 to-cyan-500'
        : 'bg-gradient-to-r from-purple-500 to-pink-500'
    }`,
    button: `w-full py-2 text-white rounded-lg text-sm font-medium transition-colors ${
      isBusinessPackage
        ? 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700'
        : 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700'
    }`
  };
};

/**
 * Calculate recommended speed based on user requirements
 * Enhanced version with improved robustness and realistic scaling
 * @param {Object} answers - User's answers to recommendation questions
 * @returns {number} - Recommended speed in Mbps
 */
export const calculateRecommendedSpeed = (answers) => {
  // Validate inputs with defaults for robustness
  const usageTypes = Array.isArray(answers.usageTypes) ? answers.usageTypes :
                    (answers.usageType ? [answers.usageType] : ['general']); // Handle both new and old format
  const usageIntensity = answers.usageIntensity || 'moderate';
  const users = Math.max(1, parseInt(answers.users) || 1);
  const devices = Math.max(1, parseInt(answers.devices) || 1);
  const activities = Array.isArray(answers.activities) ? answers.activities : [];
  
  // Standard speed tiers - maintain the same tiers for compatibility
  const speedTiers = [10, 20, 25, 50, 100, 200, 500, 1000];
  
  // 1. ENHANCED BASE SPEED CALCULATION
  
  // Calculate base speed for each selected usage type
  const baseSpeeds = usageTypes.map(usageType => {
    switch (usageType) {
      case 'general':
        return 25; // Good for basic browsing
      case 'streaming':
        return 40; // Adjusted from 50 to be more realistic for standard HD
      case 'gaming':
        return 75; // Adjusted from 100 to be more realistic
      case 'work':
        return 50; // Adjusted from 75 to be more realistic
      default:
        return 25;
    }
  });
  
  // Use the maximum as the starting point
  let baseSpeed = Math.max(...baseSpeeds, 25); // Default to 25 if empty array
  
  // Add a bonus if multiple usage types are selected to account for diverse needs
  if (usageTypes.length > 1) {
    baseSpeed = Math.round(baseSpeed * 1.2); // 20% bonus for diverse needs
  }
  
  // Adjust base speed based on usage intensity
  const intensityMultiplier =
    usageIntensity === 'light' ? 0.8 :
    usageIntensity === 'heavy' ? 1.2 : 1;
  baseSpeed = Math.round(baseSpeed * intensityMultiplier);
  
  // 2. IMPROVED USER SCALING
  // More linear scaling that better accounts for multiple simultaneous users
  // 1 user: 1x, 2 users: 1.5x, 3 users: 2x, 4 users: 2.5x, etc.
  const userMultiplier = 1 + (users - 1) * 0.5;
  
  // 3. IMPROVED DEVICE SCALING
  // Better scaling for multiple devices with a reasonable cap
  const deviceMultiplier = Math.min(2.5, 1 + Math.log10(devices + 1));
  
  // 4. ENHANCED ACTIVITY BONUSES
  // Scale activity bonuses based on user count
  let activityBonus = 0;
  
  // User scaling factor for activities - more users means more potential simultaneous usage
  const userScalingFactor = Math.min(1.5, 0.7 + (users - 1) * 0.1);
  
  // Apply activity bonuses with improved values and scaling
  if (activities.includes('4k-streaming')) activityBonus += 25 * userScalingFactor;
  if (activities.includes('video-calls')) activityBonus += 15 * userScalingFactor;
  if (activities.includes('large-downloads')) activityBonus += 40 * userScalingFactor;
  if (activities.includes('cloud-backup')) activityBonus += 20 * userScalingFactor;
  if (activities.includes('file-sharing')) activityBonus += 15 * userScalingFactor;
  
  // 5. ACCOUNT FOR SIMULTANEOUS ACTIVITIES
  // More activities happening at once need more bandwidth
  const simultaneousFactor = Math.min(1.5, 1 + (activities.length - 1) * 0.15);
  if (activities.length > 1) {
    activityBonus *= simultaneousFactor;
  }
  
  // 6. CALCULATE RAW RECOMMENDED SPEED
  const calculatedSpeed = Math.round(baseSpeed * userMultiplier * deviceMultiplier + activityBonus);
  
  // 7. INTELLIGENT TIER SELECTION
  // Find the nearest standard speed tier with smarter selection logic
  
  // First find the highest tier that's less than or equal to calculated speed
  const lowerTier = [...speedTiers].filter(tier => tier <= calculatedSpeed).pop() || speedTiers[0];
  
  // Then find the lowest tier that's greater than calculated speed
  const higherTier = speedTiers.find(tier => tier >= calculatedSpeed) || speedTiers[speedTiers.length - 1];
  
  // Determine final tier with smart selection logic
  let recommendedTier;
  
  // If calculated speed is very close to a lower tier (within 15%), use that tier
  if (calculatedSpeed <= lowerTier * 1.15) {
    recommendedTier = lowerTier;
  }
  // If calculated speed is much closer to lower tier than higher tier, use lower tier
  else if (higherTier - calculatedSpeed > calculatedSpeed - lowerTier) {
    recommendedTier = lowerTier;
  }
  // Otherwise use the higher tier
  else {
    recommendedTier = higherTier;
  }
  
  // 8. SAFETY CHECK - ensure we return a valid tier
  // If something went wrong, default to a reasonable tier
  if (!speedTiers.includes(recommendedTier)) {
    // Find the closest tier as a fallback
    recommendedTier = speedTiers.reduce((prev, curr) =>
      Math.abs(curr - calculatedSpeed) < Math.abs(prev - calculatedSpeed) ? curr : prev
    );
  }
  
  // Log the calculation details for debugging
  console.log('Speed recommendation calculation (formatters.js):', {
    usageTypes,
    usageIntensity,
    users,
    devices,
    activities,
    baseSpeed,
    userMultiplier,
    deviceMultiplier,
    activityBonus,
    simultaneousFactor: activities.length > 1 ? simultaneousFactor : 1,
    calculatedSpeed,
    recommendedTier
  });
  
  return recommendedTier;
};

/**
 * Get a human-readable explanation for the recommended speed
 * Enhanced version with more detailed and personalized explanations
 * @param {number} speed - Recommended speed in Mbps
 * @param {Object} answers - User's answers to recommendation questions
 * @returns {string} - Explanation for the recommendation
 */
export const getRecommendationExplanation = (speed, answers) => {
  // Validate inputs with defaults for robustness
  const usageTypes = Array.isArray(answers.usageTypes) ? answers.usageTypes :
                    (answers.usageType ? [answers.usageType] : ['general']); // Handle both new and old format
  const usageIntensity = answers.usageIntensity || 'moderate';
  const users = Math.max(1, parseInt(answers.users) || 1);
  const devices = Math.max(1, parseInt(answers.devices) || 1);
  const activities = Array.isArray(answers.activities) ? answers.activities : [];
  
  let explanation = `Based on your needs, we recommend a ${speed} Mbps connection. `;
  
  // Add intensity-based description
  if (usageIntensity === 'heavy') {
    explanation += 'This plan supports your intensive internet usage. ';
  } else if (usageIntensity === 'light') {
    explanation += 'This plan is well-suited for your lighter internet usage. ';
  }
  
  // Add usage type specific explanation based on multiple usage types
  if (usageTypes.length > 1) {
    explanation += 'This connection is optimized for your diverse needs, including ';
    
    usageTypes.forEach((usageType, index) => {
      if (index > 0) {
        explanation += index === usageTypes.length - 1 ? ' and ' : ', ';
      }
      
      switch (usageType) {
        case 'streaming':
          explanation += 'streaming media';
          break;
        case 'gaming':
          explanation += 'online gaming';
          break;
        case 'work':
          explanation += 'working from home';
          break;
        case 'general':
        default:
          explanation += 'everyday browsing';
      }
    });
    
    explanation += '. ';
    
    // Add specific details based on the combination of usage types
    if (usageTypes.includes('streaming') && usageTypes.includes('gaming')) {
      explanation += 'The speed provides enough bandwidth for both gaming and streaming simultaneously. ';
    } else if (usageTypes.includes('streaming') && usageTypes.includes('work')) {
      explanation += 'You\'ll be able to stream media while maintaining reliable work connections. ';
    } else if (usageTypes.includes('gaming') && usageTypes.includes('work')) {
      explanation += 'This ensures low latency for gaming while supporting your professional needs. ';
    }
  } else if (usageTypes.length === 1) {
    // Single usage type explanation (original logic)
    const usageType = usageTypes[0];
    
    switch (usageType) {
      case 'streaming':
        explanation += 'This will provide smooth streaming experiences ';
        if (activities.includes('4k-streaming')) {
          explanation += 'even for 4K content';
          if (users > 1) {
            explanation += ` across ${users} users`;
          }
          explanation += '. ';
        } else {
          explanation += 'for HD content';
          if (users > 1) {
            explanation += ` across ${users} users`;
          }
          explanation += '. ';
        }
        break;
        
      case 'gaming':
        explanation += 'This will ensure low latency for gaming ';
        if (activities.includes('large-downloads')) {
          explanation += 'and fast download speeds for game updates and new titles. ';
        } else {
          explanation += 'and a smooth online experience. ';
        }
        if (users > 1 && users <= 3) {
          explanation += `Perfect for ${users} gamers sharing the connection. `;
        } else if (users > 3) {
          explanation += `Supports multiple gamers (${users}) in your household. `;
        }
        break;
        
      case 'work':
        explanation += 'This will support your work-from-home needs ';
        if (activities.includes('video-calls')) {
          explanation += 'including high-quality video conferencing';
          if (activities.includes('cloud-backup')) {
            explanation += ' and efficient cloud backups';
          }
          explanation += '. ';
        } else if (activities.includes('cloud-backup')) {
          explanation += 'with reliable cloud backup capabilities. ';
        } else {
          explanation += 'for reliable connectivity throughout your workday. ';
        }
        break;
        
      case 'general':
      default:
        explanation += 'This speed is perfect for everyday internet activities';
        if (activities.length > 0) {
          explanation += ' while supporting your additional needs';
        }
        explanation += '. ';
    }
  } else {
    // No usage type selected (fallback)
    explanation += 'This speed is suitable for general internet usage. ';
  }
  
  // Add details about household size and devices
  if (users > 1) {
    if (devices > users) {
      // Many devices per user
      if (devices >= users * 3) {
        explanation += `It accommodates your tech-heavy household with ${users} users and ${devices} connected devices. `;
      } else {
        explanation += `It supports your ${users} users with their ${devices} devices. `;
      }
    } else {
      explanation += `It's sized appropriately for your household of ${users} users. `;
    }
  } else if (devices > 3) {
    explanation += `It handles your multiple devices (${devices} total) efficiently. `;
  }
  
  // Add details about simultaneous activities if multiple were selected
  if (activities.length > 2) {
    explanation += 'This recommendation accounts for your diverse internet activities happening simultaneously. ';
  } else if (activities.length == 2) {
    explanation += 'This plan balances your different online activities. ';
  }
  
  return explanation;
};

/**
 * Filter packages based on recommended speed
 * @param {Array} packages - Array of package objects
 * @param {number} recommendedSpeed - Recommended speed in Mbps
 * @returns {Array} - Filtered packages
 */
export const filterPackagesByRecommendedSpeed = (packages, recommendedSpeed) => {
  // Define a range around the recommended speed (±20%)
  const minSpeed = recommendedSpeed * 0.8 * 1024; // Convert to kbps
  const maxSpeed = recommendedSpeed * 1.2 * 1024; // Convert to kbps
  
  return packages.filter(pkg => {
    const downloadSpeed = pkg["Download speed (kbps)"];
    return downloadSpeed >= minSpeed && downloadSpeed <= maxSpeed;
  });
};